import styled from 'styled-components';

export const DetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px 72px;
  display: grid;
  grid-template-columns: 7fr 5fr;
  grid-template-areas:
    'images booking'
    'content booking'
    'reviews reviews';
  gap: 0 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'images'
      'booking'
      'content'
      'reviews';
  }
`;

export const ImageSection = styled.div`
  grid-area: images;
  margin-bottom: 28px;
`;

export const MainImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);

  @media (max-width: 640px) {
    height: 240px;
  }
`;

export const ImageGallery = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

export const ThumbnailImage = styled.img`
  width: 84px;
  height: 60px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  cursor: pointer;
  flex-shrink: 0;
  border: 2px solid ${props => (props.$active ? 'var(--primary)' : 'transparent')};
  opacity: ${props => (props.$active ? 1 : 0.75)};
  transition: opacity var(--transition-fast), border-color var(--transition-fast);

  &:hover { opacity: 1; }
`;

export const ContentSection = styled.div`
  grid-area: content;

  .price-row {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
  }
`;

export const TitleSection = styled.div`
  margin-bottom: 18px;

  .type-chip {
    display: inline-block;
    background: var(--primary-subtle);
    color: var(--primary);
    font-size: var(--text-xs);
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: var(--radius-round);
    margin-bottom: 12px;
  }
`;

export const Title = styled.h1`
  font-size: var(--text-3xl);
  letter-spacing: -0.5px;
  margin-bottom: 8px;
`;

export const Location = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  margin-bottom: 4px;
`;

export const Rating = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
`;

export const Price = styled.div`
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--secondary);

  span {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--text-muted);
  }
`;

export const Duration = styled.span`
  color: var(--text-secondary);
  font-size: var(--text-sm);
`;

export const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 28px;
  white-space: pre-line;
`;

export const Features = styled.div`
  margin-bottom: 28px;

  h3 {
    font-size: var(--text-lg);
    margin-bottom: 14px;
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 8px;
  }
`;

export const FeatureItem = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  padding: 8px 12px 8px 32px;
  background: var(--success-subtle);
  border-radius: var(--radius-sm);
  position: relative;

  &::before {
    content: '✓';
    position: absolute;
    left: 12px;
    color: var(--success-dark);
    font-weight: 700;
  }
`;

export const BookingSection = styled.div`
  grid-area: booking;

  @media (max-width: 900px) {
    margin-bottom: 28px;
  }
`;

export const BookingCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  padding: 26px;
  position: sticky;
  top: 92px;

  h3 {
    font-size: var(--text-lg);
    margin-bottom: 18px;
  }

  .booking-error {
    background: var(--danger-subtle);
    color: var(--danger-dark);
    font-size: var(--text-sm);
    padding: 10px 14px;
    border-radius: var(--radius-md);
    margin-bottom: 14px;
  }

  .hold-note {
    text-align: center;
    font-size: var(--text-xs);
    color: var(--text-muted);
    margin-top: 12px;
  }

  @media (max-width: 900px) {
    position: static;
  }
`;

const selectorBase = `
  margin-bottom: 16px;

  label {
    display: block;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 6px;
  }

  input, select {
    width: 100%;
    padding: 11px 13px;
    background: var(--bg-input);
    border: 1.5px solid transparent;
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-family: var(--font-brand);
    font-size: var(--text-sm);
    transition: border-color var(--transition-fast), background var(--transition-fast);

    &:focus {
      outline: none;
      background: var(--white);
      border-color: var(--border-focus);
      box-shadow: 0 0 0 4px var(--focus-ring);
    }
  }
`;

export const DateSelector = styled.div`${selectorBase}`;
export const TimeSelector = styled.div`${selectorBase}`;

export const QuantitySelector = styled.div`
  margin-bottom: 18px;

  label {
    display: block;
    font-size: var(--text-sm);
    font-weight: 600;
    margin-bottom: 6px;
  }

  .stepper {
    display: inline-flex;
    align-items: center;
    gap: 18px;
    background: var(--bg-input);
    border-radius: var(--radius-round);
    padding: 6px;

    button {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: none;
      background: var(--bg-card);
      color: var(--primary);
      font-size: var(--text-lg);
      font-weight: 700;
      cursor: pointer;
      box-shadow: var(--shadow-xs);
      transition: background var(--transition-fast), color var(--transition-fast);

      &:hover:not(:disabled) {
        background: var(--primary);
        color: var(--white);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }

    span {
      min-width: 24px;
      text-align: center;
      font-weight: 700;
      font-size: var(--text-base);
    }
  }
`;

export const TotalPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--text-lg);
  font-weight: 700;
  padding: 14px 0;
  margin-bottom: 14px;
  border-top: 1px solid var(--border-light);

  span:last-child { color: var(--secondary); }
`;

export const Reviews = styled.section`
  grid-area: reviews;
  margin-top: 20px;

  h3 {
    font-size: var(--text-xl);
    margin-bottom: 16px;
  }

  .no-reviews {
    color: var(--text-muted);
    font-size: var(--text-sm);
    background: var(--bg-section);
    border-radius: var(--radius-md);
    padding: 20px;
    text-align: center;
  }
`;

export const ReviewItem = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 18px;
  margin-bottom: 12px;
`;

export const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: var(--text-sm);

  div {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  > span { color: var(--text-muted); font-size: var(--text-xs); }
`;

export const ReviewText = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.6;
`;
